import { Box, Grid, MenuItem, Typography } from "@mui/material";
import InputDatePicker from "../../../../components/input/inputDatePicker";
import InputTextField from "../../../../components/input/inputTextField";
import { InputValueProjectForm } from "./domain";
import FilterSelect from "../../../../components/select/filterSelect";
import { useCallback, useEffect, useState } from "react";
import { YearsApi } from "../../../../api/yeas/years.api";
import CheckBoxCustom from "../../../../components/checkbox/CheckBoxCustom";
import RadioCustom from "../../../../components/radio/RadioCustom";
import moment from "moment";
import { edpex_mission_dropdown_type, edpexMissionAPI } from "../../../../api/edpex-mission/edpex-mission.api";
import { edpexStandardPointMasterAPI } from "../../../../api/edpex-standard-point-master/edpex-standard-point-master.api";
import { edpexStandardPointDetailMasterAPI } from "../../../../api/edpex-standard-point-detail-master/edpex-standard-point-detail-master.api";
import {
  EDPEX_PROJECT_DEPARTMENT_RESPONSIBLE,
  EDPEX_PROJECT_DIVISION_RESPONSIBLE,
  EDPEX_PROJECT_USER_RESPONSIBLE,
  TYPE_EDPEX_PROJECT_TYPE,
  TYPE_EDPEX_PROJECT_TYPE_ONGOING,
} from "../enum";
import { edpexProjectAPI } from "../../../../api/edpex-project/edpex-project.api";
import { alertError } from "../../../../components/alert/swal";
import { produce } from "immer";
import InputTextMask from "../../../../components/input/inputTextMask";
import AutocompleteSelect from "../../../../components/select/autoCompleteSelect";
import { debouncedApiCall } from "../../../../utils/app.utils";
import { useTranslation } from "react-i18next";

interface IProjectFormProps {
  inputValue: InputValueProjectForm;
  setInputValue: React.Dispatch<React.SetStateAction<InputValueProjectForm>>;
  disabled?: boolean
}

const MainProjectForm: React.FC<IProjectFormProps> = ({
  inputValue,
  setInputValue,
  disabled = false
}) => {
  const { t, i18n } = useTranslation();

  const [dataYears, setDataYears] = useState<any[]>([]);
  const [dataEdpexMission, setDataEdpexMission] = useState<any[]>([]);
  const [search_edpex_mission, set_search_edpex_mission] = useState<string>("");
  const [dataEdpexCriteria, setDataEdpexCriteria] = useState<any[]>([]);
  const [dataEdpexCriteriaSub, setDataEdpexCriteriaSub] = useState<any[]>([]);

  const [projectContinueData, setProjectContinueData] = useState<any>([]);

  const fetchGetYears = async () => {
    const res = await YearsApi.getYears();
    const data = res.data;
    if (data.status < 300) {
      setDataYears(data.data);
    } else {
      setDataYears([]);
    }
  };

  const fetchGetEdpexMission = useCallback(async () => {
    let query: edpex_mission_dropdown_type = {
      page: 1,
      limit: 30
    }
    if (search_edpex_mission) query.keyword = search_edpex_mission;
    const res = await edpexMissionAPI.dropdown(query);
    const data = res.data;
    if (data.status < 300) {
      const mappedData = data.data.items.map((item: any) => ({
        name: item.edpex_mission_name,
        value: item.id_edpex_mission,
      }));
      setDataEdpexMission(mappedData);
    } else {
      setDataEdpexMission([]);
    }
  }, [search_edpex_mission])

  const fetchGetEdpexCriteria = async () => {
    const res =
      await edpexStandardPointMasterAPI.getEdpexStandardPointMasterList({
        page: 1,
        limit: 100000,
      });
    const data = res.data;
    if (data.status < 300) {
      const mappedData = data.data.map((item: any) => ({
        name: item.edpex_standard_point_name,
        value: item.id_edpex_standard_point,
      }));
      setDataEdpexCriteria(mappedData);
    } else {
      setDataEdpexCriteria([]);
    }
  };

  const fetchGetEdpexCriteriaSub = async (id_edpex_standard_point: number) => {
    const res =
      await edpexStandardPointDetailMasterAPI.getEdpexStandardPointDetailMasterList(
        {
          page: 1,
          limit: 100000,
        }
      )
    const data = res.data;
    if (data.status < 300) {
      const mappedData = data.data
        .filter(
          (item: any) =>
            item.id_edpex_standard_point === id_edpex_standard_point
        )
        .map((item: any) => ({
          name: item.edpex_standard_point_detail_name || "-",
          value: item.id_edpex_standard_point_detail,
        }));
      setDataEdpexCriteriaSub(mappedData);
    } else {
      setDataEdpexCriteriaSub([]);
    }
  };

  useEffect(() => {
    fetchGetYears();
    fetchGetEdpexCriteria();
  }, []);
  useEffect(() => {
    fetchGetEdpexMission();
  }, [fetchGetEdpexMission])


  useEffect(() => {
    if (inputValue?.edpex_criteria) {
      fetchGetEdpexCriteriaSub(+inputValue?.edpex_criteria);
    }
  }, [inputValue?.edpex_criteria])

  const handleInputChange = <K extends keyof InputValueProjectForm>(field: K, value: any) => {
    // setInputValue((prev) => ({ ...prev, [key]: value }));
    setInputValue((prev) => produce(prev, (draft) => {
      draft[field] = value
    }))
  };

  const responsibleDirectorOption = [
    {
      name: EDPEX_PROJECT_USER_RESPONSIBLE.ADMIN,
      value: EDPEX_PROJECT_USER_RESPONSIBLE.ADMIN,
    },
    {
      name: EDPEX_PROJECT_USER_RESPONSIBLE.RESEARCH_AND_ACADEMIC,
      value: EDPEX_PROJECT_USER_RESPONSIBLE.RESEARCH_AND_ACADEMIC,
    },
  ];

  const responsibleDepartmentOption = [
    {
      name: EDPEX_PROJECT_DEPARTMENT_RESPONSIBLE.ACADEMIC_OFFICE,
      value: EDPEX_PROJECT_DEPARTMENT_RESPONSIBLE.ACADEMIC_OFFICE,
    },
    {
      name: EDPEX_PROJECT_DEPARTMENT_RESPONSIBLE.COLLEGE_OFFICE,
      value: EDPEX_PROJECT_DEPARTMENT_RESPONSIBLE.COLLEGE_OFFICE,
    },
  ];

  const responsibleWorkOption = [
    {
      name: EDPEX_PROJECT_DIVISION_RESPONSIBLE.ACADEMIC,
      value: EDPEX_PROJECT_DIVISION_RESPONSIBLE.ACADEMIC,
    },
    {
      name: EDPEX_PROJECT_DIVISION_RESPONSIBLE.ADMINISTRATIVE,
      value: EDPEX_PROJECT_DIVISION_RESPONSIBLE.ADMINISTRATIVE,
    },
  ];

  useEffect(() => {
    loadProjectContinueData();
  }, []);

  const loadProjectContinueData = async () => {
    try {
      await edpexProjectAPI
        .getEdpexProjectList({
          page: 1,
          limit: 10000,
        })
        .then((res) => {
          if (res.data.status < 300) {
            const projectContinueData = res.data.data;

            setProjectContinueData(projectContinueData.items ?? []);
          } else {
            alertError(res.message || "error");
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => { });
    } catch (error: any) {
      console.error("Error loading project data:", error);
      alertError(error.message || "error");
    } finally {
    }
  };

  return (
    <>
      <Grid
        item
        xs={12}
        md={12}
        lg={12}
        sx={{
          alignSelf: "end",
        }}
      >
        <InputTextField
          heading={t("ชื่อโครงการ")}
          placeholder={t("ชื่อโครงการ")}
          value={inputValue.project_name ?? ""}
          onchange={(e: any) =>
            handleInputChange("project_name", e.target.value)
          }
          required
          helperText={""}
          disabled={disabled}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
        lg={3}
        sx={{
          alignSelf: "end",
        }}
      >
        <FilterSelect
          value={inputValue.fiscal_year ?? ""}
          labelId="year-id"
          selectId="year-select-id"
          heading={t("ปีงบประมาณ")}
          options={[{ year: "ทุกปีงบปะมาณ" /* i18n-ignore (option value; label translated below) */ }, ...dataYears].map(
            (item: any, index: number) => (
              <MenuItem value={item?.year} key={index + 1}>
                {index === 0 ? t("ทุกปีงบปะมาณ") : (i18n.language === "en" ? +item?.year - 543 : item?.year)}
              </MenuItem>
            )
          )}
          onchange={(e: any) =>
            handleInputChange("fiscal_year", e.target.value)
          }
          disabled={disabled}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={3}
        sx={{
          alignSelf: "end",
        }}
      >
        <InputDatePicker
          heading={t("ระยะเวลาเริ่มต้นดำเนินการ")}
          placeholder={t("ระยะเวลาเริ่มต้นดำเนินการ")}
          value={inputValue.start_date ?? ""}
          onChange={(e: any) =>
            handleInputChange("start_date", moment(e).format("YYYY-MM-DD"))
          }
          helperText={""}
          disabled={disabled}
          disableStartDate={inputValue.fiscal_year - 544 + "-10-01"}
          disableEndDate={inputValue.fiscal_year - 543 + "-09-30"}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={3}
        sx={{
          alignSelf: "end",
        }}
      >
        <InputDatePicker
          heading={t("ระยะเวลาสิ้นสุดดำเนินการ")}
          placeholder={t("ระยะเวลาสิ้นสุดดำเนินการ")}
          value={inputValue.end_date ?? ""}
          onChange={(e: any) =>
            handleInputChange("end_date", moment(e).format("YYYY-MM-DD"))
          }
          disableStartDate={inputValue.fiscal_year - 544 + "-10-01"}
          disableEndDate={inputValue.fiscal_year - 543 + "-09-30"}
          helperText={""}
          disabled={disabled}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
        lg={3}
        sx={{
          alignSelf: "end",
        }}
      >
        <InputTextMask
          mode="amount"
          heading={t("งบประมาณ")}
          placeholder={t("งบประมาณ")}
          value={String(inputValue.budget || "") ?? ""}
          onChange={(e) => handleInputChange("budget", e.target.value)}
          required
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={12} lg={12} sx={{ alignSelf: "end" }}>
        <Typography variant="h6" gutterBottom>
          {t("รูปแบบโครงการ")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row", // optional, default for display: flex
            flexWrap: "nowrap", // ensures items stay in one line
            alignItems: "center",
          }}
        >
          {[
            {
              id: TYPE_EDPEX_PROJECT_TYPE.EDPEX_STRATEGY,
              name: t("งานยุทธศาสตร"),
            },
            {
              id: TYPE_EDPEX_PROJECT_TYPE.EDPEX_NORMAL,
              name: t("งานพื้นฐาน/งานประจำ/งานตามภารกิจ"),
            },
          ].map((item) => (
            <CheckBoxCustom
              key={item.id}
              checked={inputValue.edpex_project_strategic_work === item.id}
              label={item.name}
              setChecked={(status: boolean) => {
                handleInputChange("edpex_project_strategic_work", item.id);
              }}
              disabled={disabled}
            />
          ))}
        </Box>
      </Grid>
      <Grid item xs={12} md={12} lg={12} sx={{ alignSelf: "end" }}>
        <RadioCustom
          heading={t("สถานภาพโครงการ")}
          value={inputValue.edpex_project_ongoing ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange("edpex_project_ongoing", e.target.value);
          }}
          row
          disabled={disabled}
          dataList={[
            {
              label: t("โครงการใหม่"),
              value: TYPE_EDPEX_PROJECT_TYPE_ONGOING.NEW,
            },
            {
              label: t("โครงการต่อเนื่อง"),
              value: TYPE_EDPEX_PROJECT_TYPE_ONGOING.ON_GOING,
              renderAfter: (
                <FilterSelect
                  disabled={
                    inputValue.edpex_project_ongoing !==
                    TYPE_EDPEX_PROJECT_TYPE_ONGOING.ON_GOING
                  }
                  value={inputValue.id_project_continue ?? ""}
                  labelId="id_edpex_project-id"
                  selectId="id_edpex_project-select-id"
                  options={projectContinueData.map(
                    (item: any, index: number) => (
                      <MenuItem value={item?.id_edpex_project} key={index + 1}>
                        {item?.edpex_project_name}
                      </MenuItem>
                    )
                  )}
                  onchange={(e: any) =>
                    handleInputChange("id_project_continue", e.target.value)
                  }
                />
              ),
            },
          ]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        sx={{
          alignSelf: "end",
        }}
      >
        <FilterSelect
          value={inputValue.responsible_work ?? ""}
          labelId="ส่วนงานที่รับผิดชอบ-id"
          selectId="ส่วนงานที่รับผิดชอบ-select-id"
          heading={t("ส่วนงานที่รับผิดชอบ")}
          options={[...responsibleWorkOption].map(
            (item: any, index: number) => (
              <MenuItem value={item?.value} key={index + 1}>
                {t(item?.name)}
              </MenuItem>
            )
          )}
          onchange={(e: any) =>
            handleInputChange("responsible_work", e.target.value)
          }
          disabled={disabled}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        sx={{
          alignSelf: "end",
        }}
      >
        <FilterSelect
          value={inputValue.responsible_department ?? ""}
          labelId="สำนักงานที่รับผิดชอบ-id"
          selectId="สำนักงานที่รับผิดชอบ-select-id"
          heading={t("สำนักงานที่รับผิดชอบ")}
          options={[...responsibleDepartmentOption].map(
            (item: any, index: number) => (
              <MenuItem value={item?.value} key={index + 1}>
                {t(item?.name)}
              </MenuItem>
            )
          )}
          disabled={disabled}
          onchange={(e: any) =>
            handleInputChange("responsible_department", e.target.value)
          }
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        sx={{
          alignSelf: "end",
        }}
      >
        <FilterSelect
          value={inputValue.responsible_director ?? ""}
          labelId="รองผอ.ผู้รับผิดชอบ-id"
          selectId="รองผอ.ผู้รับผิดชอบ-select-id"
          heading={t("รองผอ.ผู้รับผิดชอบ")}
          options={[...responsibleDirectorOption].map(
            (item: any, index: number) => (
              <MenuItem value={item?.value} key={index + 1}>
                {t(item?.name)}
              </MenuItem>
            )
          )}
          disabled={disabled}
          onchange={(e: any) =>
            handleInputChange("responsible_director", e.target.value)
          }
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        sx={{
          alignSelf: "end",
        }}
      >
        {/* <FilterSelect
          value={inputValue.edpex_mission ?? ""}
          labelId="พันธกิจ-id"
          selectId="พันธกิจ-select-id"
          heading="พันธกิจ"
          options={[...dataEdpexMission].map((item: any, index: number) => (
            <MenuItem value={item?.value} key={index + 1}>
              {item?.name}
            </MenuItem>
          ))}
          disabled={disabled}
          onchange={(e: any) =>
            handleInputChange("edpex_mission", e.target.value)
          }
        /> */}
        <AutocompleteSelect
          labelId="edpex_mission"
          options={dataEdpexMission}
          value={dataEdpexMission?.find((x) => x?.value == inputValue.edpex_mission) || null}
          getOptionLabel={(op) => op?.name || ""}
          onInputChange={(_, val) => debouncedApiCall({ searchItem: val, setItem: set_search_edpex_mission })}
          heading={t("พันธกิจ")}
          disabled={disabled}
          onChange={(_, val) => handleInputChange("edpex_mission", val?.value ?? "")}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        sx={{
          alignSelf: "end",
        }}
      >
        <FilterSelect
          value={inputValue.edpex_criteria ?? ""}
          labelId="เกณฑ์-id"
          selectId="เกณฑ์-select-id"
          heading={t("เกณฑ์ Edpex")}
          options={[...dataEdpexCriteria].map((item: any, index: number) => (
            <MenuItem value={item?.value} key={index + 1}>
              {item?.name}
            </MenuItem>
          ))}
          disabled={disabled}
          onchange={(e: any) => {
            handleInputChange("edpex_criteria", e.target.value);
            handleInputChange("edpex_criteria_sub", "");
            // fetchGetEdpexCriteriaSub(+e.target.value);
          }}
        />
      </Grid>{" "}
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        sx={{
          alignSelf: "end",
        }}
      >
        <FilterSelect
          value={inputValue?.edpex_criteria_sub || ""}
          labelId="เกณฑ์คะแนน-id"
          selectId="เกณฑ์คะแนน-select-id"
          heading={t("เกณฑ์คะแนน")}
          options={dataEdpexCriteriaSub?.map((item: any, index: number) => (
            <MenuItem value={item?.value} key={index + 1}>
              {item?.name}
            </MenuItem>
          ))}
          disabled={disabled}
          onchange={(e: any) =>
            handleInputChange("edpex_criteria_sub", e.target.value)
          }
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={12}
        lg={12}
        sx={{
          alignSelf: "end",
        }}
      >
        <InputTextField
          heading={t("การดำเนินการ (วัตถุประสงค์)")}
          placeholder={t("การดำเนินการ (วัตถุประสงค์)")}
          value={inputValue.objective ?? ""}
          onchange={(e: any) => handleInputChange("objective", e.target.value)}
          helperText={""}
          disabled={disabled}
          row={3}
          maxRows={5}
          disablePaddingMultiline
          multiline
          inputHeight={"auto"}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={12}
        lg={12}
        sx={{
          alignSelf: "end",
        }}
      >
        <InputTextField
          heading={t("กลุ่มเป้าหมาย")}
          placeholder={t("กลุ่มเป้าหมาย")}
          value={inputValue.target_group ?? ""}
          onchange={(e: any) =>
            handleInputChange("target_group", e.target.value)
          }
          helperText={""}
          disabled={disabled}
          row={3}
          maxRows={5}
          disablePaddingMultiline
          multiline
          inputHeight={"auto"}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={12}
        lg={12}
        sx={{
          alignSelf: "end",
        }}
      >
        <InputTextField
          heading={t("ผลลัพธ์ EdPEx")}
          placeholder={t("ผลลัพธ์ EdPEx")}
          value={inputValue.edpex_result ?? ""}
          onchange={(e: any) =>
            handleInputChange("edpex_result", e.target.value)
          }
          helperText={""}
          disabled={disabled}
          row={3}
          maxRows={5}
          disablePaddingMultiline
          multiline
          inputHeight={"auto"}
        />
      </Grid>
    </>
  );
};

export default MainProjectForm;
